import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { useHospitalTitle } from '../../lib/hospital-brand';
import { formatDate } from '../../lib/datetime';
import { useDocumentHeaderLogo } from '../settings/settings.queries';
import { useIpdDaycareAdmission, useIpdDischargeSummary } from './ipd-daycare.queries';
import { formatIpdDateTime } from './ipd-daycare.utils';

const DISCHARGE_HEADER_NAME = 'discharge_summery';
const upper = (value: string | number | null | undefined) => (value === null || value === undefined || value === '' ? '-' : String(value).toUpperCase());

type ContentSection = { title: string; body: string | null; html?: boolean };

export const IpdDischargePrintPage = () => {
  const { admissionId } = useParams();
  const [params] = useSearchParams();
  const withHeader = params.get('header') === '1';
  const hospitalTitle = useHospitalTitle();
  const admissionQuery = useIpdDaycareAdmission(admissionId);
  const dischargeQuery = useIpdDischargeSummary(admissionId);
  const headerLogoQuery = useDocumentHeaderLogo(DISCHARGE_HEADER_NAME, withHeader);

  useEffect(() => {
    if (admissionQuery.data && dischargeQuery.data && (!withHeader || headerLogoQuery.isFetched)) window.setTimeout(() => window.print(), 300);
  }, [admissionQuery.data, dischargeQuery.data, withHeader, headerLogoQuery.isFetched]);

  if (admissionQuery.isLoading || dischargeQuery.isLoading) return <LoadingState />;
  if (admissionQuery.isError || !admissionQuery.data) return <ErrorState title="Admission not found" message="The admission could not be loaded." />;
  if (!dischargeQuery.data) return <EmptyState title="No discharge summary yet" message="A discharge summary has not been created for this admission. Use Discharge Summary to create one first." />;

  const admission = admissionQuery.data;
  const discharge = dischargeQuery.data;
  const patient = admission.patientSnapshot;
  const address = [patient.address, patient.district, patient.state, patient.pinCode].filter(Boolean).join(', ') || '-';
  const age = `${patient.ageYears}Y ${patient.ageMonths}M ${patient.ageDays}D`;
  const headerLogo = withHeader ? headerLogoQuery.data : null;

  const sections: ContentSection[] = [
    { title: 'Final Diagnosis at the time of Discharge', body: discharge.finalDiagnosis, html: true },
    { title: 'Presenting Complaints with Duration and Reason for Admission', body: discharge.presentingComplaints, html: true },
    { title: 'Summary of Presenting Illness', body: discharge.presentingIllnessSummary, html: true },
    { title: 'Key findings on physical examination at the time of admission', body: discharge.physicalExaminationAtAdmission, html: true },
    { title: 'Summary of key investigations during Hospitalization', body: discharge.investigationsSummary, html: true },
    { title: 'Course in the Hospital including complications if any', body: discharge.courseInHospital, html: true },
    { title: 'Advice on Discharge', body: discharge.dischargeAdvice, html: true },
    ...(discharge.dischargeType === 'Referral' ? [{ title: 'Referral Hospital', body: discharge.referralHospitalName }] : []),
    { title: 'Remark', body: discharge.remark, html: true },
  ].filter((section): section is ContentSection => Boolean(section.body));

  return (
    <div className="print-discharge-summary">
      {withHeader ? (
        headerLogo ? (
          <div className="print-discharge-summary-logo"><img src={headerLogo} alt={hospitalTitle} /></div>
        ) : (
          <div className="print-discharge-summary-title-fallback">{hospitalTitle}</div>
        )
      ) : null}

      <h1 className="print-discharge-summary-heading">DISCHARGE SUMMARY</h1>

      <table className="print-discharge-summary-header">
        <tbody>
          <tr>
            <td>
              <p><b>Patient&apos;s Name : </b>{upper(patient.name)}</p>
              <p><b>Mobile No : </b>{upper(patient.phone)}</p>
              <p><b>Sex : </b>{upper(patient.gender)}, <b>Age : </b>{upper(age)}</p>
              {patient.guardianName ? <p><b>Guardian Name : </b>{upper(patient.guardianName)}</p> : null}
              {patient.relation ? <p><b>Guardian Relation : </b>{upper(patient.relation)}</p> : null}
              <p><b>Address : </b>{upper(address)}</p>
              <p><b>Aadhar Card No : </b>{upper(patient.aadhaar)}</p>
            </td>
            <td>
              <p><b>Admission Date : </b>{upper(formatIpdDateTime(admission.admissionDate))}</p>
              <p><b>Patient Type : </b>{upper(admission.insuranceType)}</p>
              <p><b>Bed : </b>{upper(admission.bed.name)} <b>Ward : </b>{upper(admission.bedCategory.name)}</p>
              <p><b>Under Doctor : </b>{admission.underDoctor ? `DR. ${upper(admission.underDoctor.name)}` : '-'}</p>
              {admission.otherUnderDoctor ? <p><b>Others Under Doctor : </b>DR. {upper(admission.otherUnderDoctor.name)}</p> : null}
              {admission.diagnosisAtAdmission ? <p><b>Admission Time Diagnosis : </b><span dangerouslySetInnerHTML={{ __html: admission.diagnosisAtAdmission }} /></p> : null}
              <p><b>Discharged Date : </b>{upper(formatIpdDateTime(discharge.dischargeDate))}</p>
              <p><b>Discharged Type : </b>{upper(discharge.dischargeType)}</p>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="print-discharge-summary-content">
        {sections.map((section) => (
          <div className="print-discharge-summary-section" key={section.title}>
            <p className="print-discharge-summary-section-title"><span className="print-discharge-summary-bullet" aria-hidden="true" />{section.title}</p>
            {section.html
              ? <div className="print-discharge-summary-section-body" dangerouslySetInnerHTML={{ __html: section.body ?? '' }} />
              : <p className="print-discharge-summary-section-body">{section.body}</p>}
          </div>
        ))}
        {discharge.nextAppointmentDate ? (
          <p className="print-discharge-summary-section-title"><span className="print-discharge-summary-bullet" aria-hidden="true" />Next Followup Date : {formatDate(discharge.nextAppointmentDate)}</p>
        ) : null}
      </div>

      <div className="print-discharge-summary-signoff">
        <span className="print-discharge-summary-signoff-line">Patient/Guardian Signature</span>
        <span className="print-discharge-summary-signoff-spacer" />
        <span className="print-discharge-summary-signoff-line">Signed by Doctor</span>
      </div>
      <p className="print-discharge-summary-prepared-by">
        PREPARED BY : {discharge.finalized ? (discharge.finalizedBy?.name ?? discharge.createdBy.name) : discharge.createdBy.name}
      </p>
    </div>
  );
};
